// =============================================================================
// domain/eduMarkdown.ts — the explainer articles, parsed into a tree.
// -----------------------------------------------------------------------------
// ── 🚨 A TREE, NOT AN HTML STRING ──
//
// The old client's renderer escapes the source and then builds markup by hand:
// `<strong>$1</strong>`, `<a href="…">`, and the drawer sets it with `innerHTML`.
// That is safe only for exactly as long as the escape is right, and it is a
// pattern this client has already refused once — the AI verdict renders the
// model's emphasis as elements for the same reason (D113).
//
// So this returns DATA. React renders each span as an element and there is no
// markup path at all, which means the question of escaping never arises. It also
// makes the parser testable without a DOM.
//
// ── ⚠ A DELIBERATELY SMALL SUBSET ──
//
// Headings, paragraphs, bullet and numbered lists, pipe tables, rules, and four
// inline forms. That is what the articles use. Anything else falls through as
// plain text rather than being silently dropped — a sentence rendered without
// its emphasis is a small loss; a sentence that vanishes is a different article.
//
// ── ⚠ LINK SCHEMES ARE CHECKED HERE, NOT AT THE RENDER SITE ──
//
// `[read this](javascript:…)` is the one construct in this subset that could do
// something. Nothing in the shipped registry uses a link at all, so the check
// costs nothing today and is the difference between a footgun and a parser the
// next person can extend without thinking about it.
// =============================================================================

export type EduSpan =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'em'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string };

export type EduBlock =
  /** `#` → 2, `##` → 3, `###` → 4. The drawer's own chrome is the h1. */
  | { kind: 'h'; level: 2 | 3 | 4; spans: EduSpan[] }
  | { kind: 'p'; spans: EduSpan[] }
  | { kind: 'ul'; items: EduSpan[][] }
  | { kind: 'ol'; items: EduSpan[][] }
  | { kind: 'table'; head: EduSpan[][]; rows: EduSpan[][][] }
  | { kind: 'hr' };

/**
 * Link, bold, code, italic — in that order, and the order is load-bearing.
 *
 * ⚠ BOLD MUST COME BEFORE ITALIC. `**frozen**` offered to the italic branch first
 * matches the empty run between the asterisks, and the sentence renders with a
 * stray `*` in the middle of it. One alternation with bold first consumes the
 * whole run, so a plain `*word*` is still reached.
 */
const INLINE = /\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*([^*\n]+)\*\*|`([^`\n]+)`|\*([^*\n]+)\*/g;

const SAFE_SCHEME = /^(?:https?:|mailto:)/i;

const push = (out: EduSpan[], s: EduSpan): void => {
  if (s.text !== '') out.push(s);
};

/** One line of prose → spans. */
export function eduSpans(src: string): EduSpan[] {
  const out: EduSpan[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(src)) !== null) {
    if (m.index > last) push(out, { kind: 'text', text: src.slice(last, m.index) });
    if (m[1] !== undefined && m[2] !== undefined) {
      // ⚠ An unsafe scheme keeps its TEXT and loses its link. Dropping the whole
      // construct would delete a sentence the author wrote.
      if (SAFE_SCHEME.test(m[2])) push(out, { kind: 'link', text: m[1], href: m[2] });
      else push(out, { kind: 'text', text: m[1] });
    } else if (m[3] !== undefined) push(out, { kind: 'strong', text: m[3] });
    else if (m[4] !== undefined) push(out, { kind: 'code', text: m[4] });
    else if (m[5] !== undefined) push(out, { kind: 'em', text: m[5] });
    last = m.index + m[0].length;
  }
  if (last < src.length) push(out, { kind: 'text', text: src.slice(last) });
  return out;
}

const BLANK = /^\s*$/;
const HEAD = /^(#{1,3})\s+(.+)$/;
const RULE = /^---+$/;
const BULLET = /^\s*[-*]\s+/;
const NUMBER = /^\s*\d+\.\s+/;
const ROW = /^\s*\|.*\|\s*$/;
const DIVIDER = /^\s*\|[\s\-:|]+\|\s*$/;

const cells = (line: string): EduSpan[][] =>
  line.trim().replace(/^\||\|$/g, '').split('|').map((c) => eduSpans(c.trim()));

/**
 * An article's source → the blocks that make it up.
 *
 * ⚠ A PARAGRAPH RUNS UNTIL A BLANK LINE **OR THE NEXT BLOCK STARTS**. Without the
 * second half a heading immediately under a sentence is swallowed into it, and
 * the article loses its structure while still reading as though nothing is wrong.
 */
export function eduBlocks(src: string): EduBlock[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: EduBlock[] = [];
  let i = 0;

  const starts = (s: string): boolean =>
    HEAD.test(s) || RULE.test(s.trim()) || ROW.test(s)
    || BULLET.test(s) || NUMBER.test(s);

  while (i < lines.length) {
    const line = lines[i] as string;
    if (BLANK.test(line)) { i += 1; continue; }

    const h = HEAD.exec(line);
    if (h) {
      const level = Math.min((h[1] as string).length + 1, 4) as 2 | 3 | 4;
      out.push({ kind: 'h', level, spans: eduSpans((h[2] as string).trim()) });
      i += 1; continue;
    }

    if (RULE.test(line.trim())) { out.push({ kind: 'hr' }); i += 1; continue; }

    // A table needs its divider row — `| a | b |` alone is a paragraph.
    if (ROW.test(line) && i + 1 < lines.length && DIVIDER.test(lines[i + 1] as string)) {
      const head = cells(line);
      i += 2;
      const rows: EduSpan[][][] = [];
      while (i < lines.length && ROW.test(lines[i] as string)) {
        rows.push(cells(lines[i] as string));
        i += 1;
      }
      out.push({ kind: 'table', head, rows });
      continue;
    }

    if (BULLET.test(line)) {
      const items: EduSpan[][] = [];
      while (i < lines.length && BULLET.test(lines[i] as string)) {
        items.push(eduSpans((lines[i] as string).replace(BULLET, '').trim()));
        i += 1;
      }
      out.push({ kind: 'ul', items });
      continue;
    }

    if (NUMBER.test(line)) {
      const items: EduSpan[][] = [];
      while (i < lines.length && NUMBER.test(lines[i] as string)) {
        items.push(eduSpans((lines[i] as string).replace(NUMBER, '').trim()));
        i += 1;
      }
      out.push({ kind: 'ol', items });
      continue;
    }

    const para = [line];
    i += 1;
    while (i < lines.length && !BLANK.test(lines[i] as string) && !starts(lines[i] as string)) {
      para.push(lines[i] as string);
      i += 1;
    }
    out.push({ kind: 'p', spans: eduSpans(para.join(' ').trim()) });
  }

  return out;
}
